import { balance } from './balance';
import { makeRng, newSeed, pick, shuffled } from './random';

/**
 * A curated bank of reactions for balancing practice, by reaction type.
 *
 * Every entry is stored unbalanced. The answer is not stored with it: the
 * balancer works it out, which means the bank can never drift out of step with
 * the solver, and a wrong entry shows up as a failing test rather than as a
 * wrong answer in front of him.
 */

export type ReactionType =
  | 'synthesis'
  | 'decomposition'
  | 'combustion'
  | 'single-replacement'
  | 'double-replacement';

export const REACTION_TYPE_LABELS: Record<ReactionType, string> = {
  synthesis: 'Synthesis',
  decomposition: 'Decomposition',
  combustion: 'Combustion',
  'single-replacement': 'Single replacement',
  'double-replacement': 'Double replacement',
};

export interface BankEntry {
  type: ReactionType;
  reactants: string[];
  products: string[];
}

export const REACTION_BANK: BankEntry[] = [
  // Synthesis
  { type: 'synthesis', reactants: ['H2', 'O2'], products: ['H2O'] },
  { type: 'synthesis', reactants: ['N2', 'H2'], products: ['NH3'] },
  { type: 'synthesis', reactants: ['Na', 'Cl2'], products: ['NaCl'] },
  { type: 'synthesis', reactants: ['Fe', 'O2'], products: ['Fe2O3'] },
  { type: 'synthesis', reactants: ['Mg', 'O2'], products: ['MgO'] },
  { type: 'synthesis', reactants: ['Al', 'Cl2'], products: ['AlCl3'] },
  { type: 'synthesis', reactants: ['CaO', 'H2O'], products: ['Ca(OH)2'] },
  { type: 'synthesis', reactants: ['SO3', 'H2O'], products: ['H2SO4'] },
  { type: 'synthesis', reactants: ['P4', 'O2'], products: ['P4O10'] },

  // Decomposition
  { type: 'decomposition', reactants: ['KClO3'], products: ['KCl', 'O2'] },
  { type: 'decomposition', reactants: ['CaCO3'], products: ['CaO', 'CO2'] },
  { type: 'decomposition', reactants: ['H2O2'], products: ['H2O', 'O2'] },
  { type: 'decomposition', reactants: ['NaHCO3'], products: ['Na2CO3', 'H2O', 'CO2'] },
  { type: 'decomposition', reactants: ['NH4NO3'], products: ['N2O', 'H2O'] },
  { type: 'decomposition', reactants: ['Al2O3'], products: ['Al', 'O2'] },
  { type: 'decomposition', reactants: ['PbO2'], products: ['PbO', 'O2'] },

  // Combustion
  { type: 'combustion', reactants: ['CH4', 'O2'], products: ['CO2', 'H2O'] },
  { type: 'combustion', reactants: ['C3H8', 'O2'], products: ['CO2', 'H2O'] },
  { type: 'combustion', reactants: ['C2H6', 'O2'], products: ['CO2', 'H2O'] },
  { type: 'combustion', reactants: ['C8H18', 'O2'], products: ['CO2', 'H2O'] },
  { type: 'combustion', reactants: ['C2H5OH', 'O2'], products: ['CO2', 'H2O'] },
  { type: 'combustion', reactants: ['C6H12O6', 'O2'], products: ['CO2', 'H2O'] },
  { type: 'combustion', reactants: ['C4H10', 'O2'], products: ['CO2', 'H2O'] },

  // Single replacement
  { type: 'single-replacement', reactants: ['Zn', 'HCl'], products: ['ZnCl2', 'H2'] },
  { type: 'single-replacement', reactants: ['Al', 'HCl'], products: ['AlCl3', 'H2'] },
  { type: 'single-replacement', reactants: ['Mg', 'H2SO4'], products: ['MgSO4', 'H2'] },
  { type: 'single-replacement', reactants: ['Fe', 'CuSO4'], products: ['FeSO4', 'Cu'] },
  { type: 'single-replacement', reactants: ['Cl2', 'NaBr'], products: ['NaCl', 'Br2'] },
  { type: 'single-replacement', reactants: ['Na', 'H2O'], products: ['NaOH', 'H2'] },
  { type: 'single-replacement', reactants: ['Al', 'Fe2O3'], products: ['Al2O3', 'Fe'] },

  // Double replacement
  { type: 'double-replacement', reactants: ['AgNO3', 'NaCl'], products: ['AgCl', 'NaNO3'] },
  { type: 'double-replacement', reactants: ['BaCl2', 'Na2SO4'], products: ['BaSO4', 'NaCl'] },
  { type: 'double-replacement', reactants: ['Pb(NO3)2', 'KI'], products: ['PbI2', 'KNO3'] },
  { type: 'double-replacement', reactants: ['HCl', 'NaOH'], products: ['NaCl', 'H2O'] },
  { type: 'double-replacement', reactants: ['H2SO4', 'NaOH'], products: ['Na2SO4', 'H2O'] },
  { type: 'double-replacement', reactants: ['Ca(OH)2', 'HCl'], products: ['CaCl2', 'H2O'] },
  { type: 'double-replacement', reactants: ['Na2CO3', 'HCl'], products: ['NaCl', 'H2O', 'CO2'] },
  { type: 'double-replacement', reactants: ['Al2(SO4)3', 'NaOH'], products: ['Al(OH)3', 'Na2SO4'] },
];

export interface BalancingPractice {
  seed: string;
  type: ReactionType;
  reactants: string[];
  products: string[];
  /** The unbalanced equation as shown. */
  skeleton: string;
  /** The unique smallest whole-number coefficients. */
  answer: bigint[];
}

/**
 * Draws a practice problem. The order of the species is shuffled so the same
 * reaction does not always look identical, and the answer is derived by the
 * balancer rather than stored.
 */
export function drawBalancingPractice(
  seed: string = newSeed(),
  type?: ReactionType,
): BalancingPractice {
  const rng = makeRng(`balance:${seed}`);
  const pool = type ? REACTION_BANK.filter((e) => e.type === type) : REACTION_BANK;
  if (pool.length === 0) throw new Error(`No reactions of type "${type}".`);

  const entry = pick(rng, pool);
  // Shuffling only within a side keeps the chemistry the same while changing
  // how the question looks.
  const reactants = shuffled(rng, entry.reactants);
  const products = shuffled(rng, entry.products);
  const skeleton = `${reactants.join(' + ')} → ${products.join(' + ')}`;

  const result = balance(`${reactants.join(' + ')} -> ${products.join(' + ')}`);
  if (!result.ok) {
    throw new Error(`Bank entry does not balance: ${skeleton} — ${result.message}`);
  }

  return { seed, type: entry.type, reactants, products, skeleton, answer: result.coefficients };
}

/** Compares typed coefficients against the unique smallest solution. */
export function checkCoefficients(
  practice: BalancingPractice,
  typed: Array<number | null>,
): { correct: boolean; message: string } {
  if (typed.length !== practice.answer.length || typed.some((v) => v === null)) {
    return { correct: false, message: 'Fill in every coefficient.' };
  }
  if (typed.some((v) => !Number.isInteger(v) || (v as number) < 1)) {
    return { correct: false, message: 'Coefficients are whole numbers, one or more.' };
  }

  const values = typed as number[];
  const expected = practice.answer.map(Number);

  if (values.every((v, i) => v === expected[i])) {
    return { correct: true, message: 'Correct — and in the smallest whole numbers.' };
  }

  // A correct but unreduced answer deserves a different message from a wrong one.
  const ratio = values[0]! / expected[0]!;
  const proportional = Number.isFinite(ratio) && values.every((v, i) => v === expected[i]! * ratio);
  if (proportional && Number.isInteger(ratio) && ratio > 1) {
    return {
      correct: false,
      message: `That balances, but every coefficient is ${ratio} times larger than it needs to be. Divide through by ${ratio}.`,
    };
  }

  return { correct: false, message: 'Not balanced yet — check each element in turn.' };
}
